/**
 * @file Renders the drawer for editing an investment's profile through the investment form.
 */
import { useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";

import { FUND } from "@api/services/backstage/shapes/ProjectTypeEnum";
import { Drawer } from "@components/Page";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import {
  updateInvestmentOperation as updateOperation,
  indexAccountInvestmentsOperation,
} from "@api/services/investments";

import InvestmentForm from "../../../InvestmentForm";
import type { Account, DrawerRef, Investment, OutletContext, UpdateInvestment } from "../../types";

const LABEL_SAVE = "Save";
const LABEL_TITLE = "Edit Investment";

const STATUS_ACTIVE = "ACTIVE";

type InvestmentsOutput = {
  data: Investment[];
};

type Props = {
  drawer: DrawerRef;
  account: Account;
  investment: Investment;
  updateInvestment: UpdateInvestment;
};

/**
 * Renders the investment edit drawer, loading the account's other investments before it opens.
 */
const InvestmentProfileUpdateDrawer = ({
  drawer,
  account,
  investment,
  updateInvestment,
}: Props) => {
  const [ block, setBlock ] = useState<ReactNode>();
  const [ otherInvestments, setOtherInvestments ] = useState<Investment[]>([]);

  const { request } = useAppContext() as AppContext;
  const { getProject, organization } = useOutletContext<OutletContext>();

  const status = STATUS_ACTIVE;

  const { profiles: existingProfiles } = account;
  const { projectId, investorAccountId } = investment;

  /**
   * Loads the investor's other active investments in the project before the drawer opens.
   */
  const onOpen = () =>
    request<InvestmentsOutput>(indexAccountInvestmentsOperation, { projectId, investorAccountId, status })
      .then(({ data }) => setOtherInvestments(data));

  const project = getProject(projectId);

  if (!project) {
    return null;
  }

  const { tiers = [], type } = project;
  const isFund = type === FUND;

  const isTypeSelectDisabled = false;

  /**
   * Applies the updated investment and closes the drawer.
   */
  const onAfterSubmit = (updatedInvestment: Investment) => {
    updateInvestment(updatedInvestment);
    drawer.current.close();
  };

  const investmentFormProps = {
    submitLabel: LABEL_SAVE,
    tiers,
    block,
    isFund,
    setBlock,
    investment,
    organization,
    onAfterSubmit,
    updateOperation,
    existingProfiles,
    otherInvestments,
    isTypeSelectDisabled,
  };

  return (
    <Drawer
      title={LABEL_TITLE}
      drawer={drawer}
      onOpen={onOpen}
    >
      <InvestmentForm
        {...investmentFormProps}
      />
    </Drawer>
  );
};

export default InvestmentProfileUpdateDrawer;
